import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { useAuth } from "$/lib/providers/AuthProvider";
import type {
  AccountsResponse,
  AddItemResponse,
  ItemRefreshStatusResponse,
  SyncStatusResponse,
} from "$/lib/types/bridge-apis.types";
import { restApiClient } from "$/lib/utils/restApiClient";

const apiEndPoint = "bridge";

export async function generateAddItemUrl(
  itemId?: string,
): Promise<AddItemResponse> {
  const query = itemId ? `?itemId=${encodeURIComponent(itemId)}` : "";
  return restApiClient.get(`${apiEndPoint}/generate-item-url${query}`);
}

export function useGenerateAddItemUrl() {
  return useMutation({
    mutationFn: generateAddItemUrl,
    onSuccess(data) {
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error("Error generating add item URL:", error);
      toast.error("Une erreur s'est produite, veuillez nous contacter.");
    },
  });
}

export async function generateNewConnectionUrl(): Promise<AddItemResponse> {
  return restApiClient.get(`${apiEndPoint}/generate-new-connection-url`);
}

export function useGenerateNewConnectionUrl() {
  return useMutation({
    mutationFn: generateNewConnectionUrl,
    onSuccess(data) {
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error("Error generating new connection URL:", error);
      toast.error("Une erreur s'est produite, veuillez nous contacter.");
    },
  });
}

export async function fetchAccountsByItemId(
  itemId: string,
): Promise<AccountsResponse> {
  return restApiClient.get(`${apiEndPoint}/fetch-accounts/${itemId}`);
}

export function useFetchAccountsByItemId(itemId: string) {
  return useQuery({
    queryKey: ["fetchAccounts", itemId],
    queryFn: () => fetchAccountsByItemId(itemId),
    enabled: !!itemId, // Prevent the query from running if itemId is falsy
  });
}

export async function checkItemRefreshStatus(
  itemId: number,
  accountId?: string,
): Promise<ItemRefreshStatusResponse> {
  return restApiClient.get(`${apiEndPoint}/item-status`, {
    query: { itemId, accountId },
  });
}

export function useCheckItemRefreshStatus(
  itemId: number,
  accountId?: string,
  enabled = false,
) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["checkItemRefreshStatus", itemId, accountId],
    queryFn: () => checkItemRefreshStatus(itemId, accountId),
    enabled: enabled && !!itemId,
    refetchInterval: (query) => {
      const data = query.state.data;

      if (data && data.status !== "processing") {
        queryClient.refetchQueries({ queryKey: ["me"] });
        return false;
      }
      return 3000;
    },
  });
}

export async function getSyncStatus(): Promise<SyncStatusResponse> {
  return restApiClient.get(`${apiEndPoint}/sync-status`);
}

export function useSyncStatus(enabled = false) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["syncStatus"],
    queryFn: getSyncStatus,
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data;

      // Stop polling only on a terminal state. "unknown"/null means the sync
      // status has not been written yet (the webhook/refresh may not have run
      // yet) — treating that as "stop" made polling quit on the very first tick,
      // before the sync had a chance to report "processing".
      if (data && (data.status === "done" || data.status === "failed")) {
        queryClient.refetchQueries({
          queryKey: ["me"],
        });
        return false;
      }
      // Keep polling every 3 seconds while syncing (or while status is unknown).
      return 3000;
    },
  });
}

export async function deleteBridgeItem(
  itemId: number,
): Promise<{ success: boolean }> {
  return restApiClient.delete(`${apiEndPoint}/items/${itemId}`);
}

export function useDeleteBridgeItem() {
  const queryClient = useQueryClient();
  const { invalidateUser } = useAuth();

  return useMutation({
    mutationFn: deleteBridgeItem,
    onSuccess: () => {
      invalidateUser();
      queryClient.refetchQueries({ queryKey: ["fetchTransactionsSummary"] });
      queryClient.refetchQueries({
        queryKey: ["fetchTransactionsTotalSummary"],
      });
      queryClient.refetchQueries({ queryKey: ["fetchTransactionsTable"] });
      queryClient.refetchQueries({ queryKey: ["getUserAccounts"] });
      toast.success("Connexion bancaire supprimée avec succès.");
    },
    onError: () => {
      toast.error(
        "Une erreur s'est produite lors de la suppression de la connexion bancaire.",
      );
    },
  });
}
